import { FaUserTie, FaEye, FaEyeSlash } from 'react-icons/fa';
import { PiPasswordFill } from 'react-icons/pi';
import { HiInformationCircle } from 'react-icons/hi';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { authApi } from '../../utils/api';
import { isDevelopment, env } from '../../config/env';
import './login.scss';
const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation(['auth', 'common']);
  const { direction } = useLanguage();

  const goToHome = () => {
    navigate("/");
  };

  const [formData, setFormData] = useState({
    phoneOrEmail: "",
    password: "",
  });

  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when user starts typing
    if (error) setError("");
  };
  

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // Basic validation
    if (!formData.phoneOrEmail || !formData.password) {
      setError(t('auth:validation.fillAllFields'));
      setIsLoading(false);
      return;
    }

    try {
      const response = await authApi.login(formData);

      if (response.success && response.data) {
        const result = response.data;
        console.log('Login successful:', result);

        // Use the new AuthContext login method
        login(
          result.token,
          {
            name: result.user.name,
            email: result.user.email,
            phone: result.user.phone,
          },
          result.role,
          result.permissions
        );

        goToHome();
      } else {
        // Handle error messages with translation support
        const errorMessage = response.error || response.message;
        if (errorMessage === 'BadCredentials') {
          setError(t('auth:errors.invalidCredentials'));
        } else if (errorMessage === 'AccountBanned') {
          setError(t('auth:errors.accountBanned'));
        } else if (errorMessage === 'ConfirmOTP') {
          setError(t('auth:errors.confirmOTP'));
        } else if (errorMessage === 'NotAnAdmin') {
          setError(t('auth:errors.notAnAdmin', 'Access denied. This account is not an admin account.'));
        } else {
          setError(errorMessage || t('auth:errors.loginFailed'));
        }
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(t('auth:errors.networkError'));
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className={`auth-container ${direction === 'rtl' ? 'rtl' : 'ltr'}`}>
      <div className="auth-background">
        <div className="auth-overlay"></div>
      </div>

      <div className="auth-content">
        <div className="auth-card">
          <div className="auth-header">
            <div className="logo-container">
              <img src="/logo.png" alt="9AR Admin" className="logo" style={{ width: '100px', height: 'auto' }}/>
            </div>
            <h2 className="auth-title">{t('auth:login.title', 'Welcome Back')}</h2>
            <p className="auth-subtitle">{t('auth:login.subtitle', 'Sign in to your admin account')}</p>
          </div>

          <form className="auth-form" onSubmit={handleSubmit}>
            {isDevelopment() && (
              <div className="dev-notice">
                <HiInformationCircle />
                <span>{t('auth:login.developmentMode')} {env.apiBaseUrl}</span>
              </div>
            )}

            {error && (
              <div className="error-message">
                <span className="error-text">{error}</span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="phoneOrEmail" className="form-label">
                {t('auth:login.phoneOrEmail', 'Phone or Email')}
              </label>
              <div className="input-wrapper">
                <FaUserTie className="input-icon" />
                <input
                  type="text"
                  name="phoneOrEmail"
                  id="phoneOrEmail"
                  className="form-input"
                  placeholder={t('auth:login.phoneOrEmail', 'Enter phone or email')}
                  value={formData.phoneOrEmail}
                  onChange={handleChange}
                  required
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                {t('auth:login.password', 'Password')}
              </label>
              <div className="input-wrapper">
                <PiPasswordFill className="input-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  id="password"
                  className="form-input"
                  placeholder={t('auth:login.password', 'Enter password')}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div className="form-options">
              <label className="checkbox-label">
                <input type="checkbox" className="checkbox-input" />
                <span className="checkbox-text">{t('auth:login.rememberMe', 'Remember me')}</span>
              </label>
              {/* <Link to="/auth/forgotpassword" className="forgot-link">
                {t('auth:login.forgotPassword', 'Forgot Password?')}
              </Link> */}
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="loading-spinner"></span>
              ) : (
                t('auth:login.signIn', 'Sign In')
              )}
            </button>

            <div className="auth-footer">
              {/* Admin doesn't need register link */}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
export default Login;